/**
 * Feed mode (§5.1 #26, §5.4, PRD Feed mode).
 *
 * Infinite scroll over the server's opaque keyset cursor — the feed is append-scrolled, never
 * jumped into, which is exactly why it uses cursors instead of offsets (ADR-006). Sorting lives
 * entirely in the header's Sort control: the feed endpoint accepts every posts sort key in both
 * directions, so there is no second, narrower on-page control to contradict it. The value it reads
 * is the view's `feedSort` — this tab's own ordering, unaffected by how the Posts table is sorted
 * (§12.3). Changing the sort changes `baseQuery`, hence `QK.viewFeed`, so the infinite query
 * restarts from page one and stale cursors are never replayed under a new ordering.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { MAX_FEED_LIMIT, type FeedQuery, type SubjectType, type ViewFilterParams } from '@lw/shared';
import { getViewFeed } from '../../../api/views';
import { QK } from '../../../api/queryKeys';
import { EmptyState, ErrorState, Spinner } from '../../../components';
import { useInView } from '../../../lib/hooks';
import { usePostPreview } from '../PreviewContext';
import { type ViewSort } from '../viewState';
import { FeedCard } from './FeedCard';

export type FeedModeProps = {
  subjectType: SubjectType;
  id: number;
  params: Partial<ViewFilterParams>;
  sort: ViewSort;
  enabled: boolean;
  mediaEnabled: boolean;
  hasFilters: boolean;
  onClearFilters: () => void;
};

export function FeedMode({
  subjectType,
  id,
  params,
  sort,
  enabled,
  mediaEnabled,
  hasFilters,
  onClearFilters,
}: FeedModeProps) {
  const { openPreview } = usePostPreview();
  const baseQuery: Partial<FeedQuery> = {
    ...params,
    sort: sort.key,
    dir: sort.dir,
    limit: MAX_FEED_LIMIT,
  };

  const feed = useInfiniteQuery({
    queryKey: QK.viewFeed(subjectType, id, baseQuery),
    queryFn: ({ pageParam, signal }) =>
      getViewFeed(subjectType, id, { ...baseQuery, cursor: pageParam ?? undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });

  const cards = feed.data?.pages.flatMap((page) => page.cards) ?? [];
  const ids = cards.map((card) => card.id);

  const sentinelRef = useInView<HTMLDivElement>(
    feed.hasNextPage && !feed.isFetchingNextPage,
    () => void feed.fetchNextPage(),
    { rootMargin: '400px' },
  );

  return (
    <div className="feed">
      {feed.isPending ? <Spinner size="lg" label="Loading feed…" /> : null}

      {feed.isError ? (
        <ErrorState
          title="Couldn't load the feed"
          error={feed.error}
          onRetry={() => void feed.refetch()}
          retrying={feed.isFetching}
        />
      ) : null}

      {feed.isSuccess && cards.length === 0 ? (
        <EmptyState
          title={hasFilters ? 'No posts match these filters' : 'Nothing in this range'}
          description={
            hasFilters
              ? 'Clear the filters to see the full feed.'
              : 'Widen the date range, or scrape this subject to collect posts.'
          }
          action={
            hasFilters ? (
              <button type="button" className="btn-primary" onClick={onClearFilters}>
                Clear filters
              </button>
            ) : null
          }
        />
      ) : null}

      <div className="feed__list">
        {cards.map((card) => (
          <FeedCard
            key={card.id}
            card={card}
            mediaEnabled={mediaEnabled}
            onOpen={(postId) => openPreview(postId, ids)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="feed__sentinel">
        {feed.isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        {!feed.hasNextPage && cards.length > 0 ? (
          <span className="muted">End of the feed.</span>
        ) : null}
      </div>
    </div>
  );
}

export default FeedMode;
